import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ParamIcon } from '../Soil/ParamIcon';
import { useThresholds } from '../../hooks/useThresholds';
import { useWeatherThresholds } from '../../hooks/useWeatherThresholds';
import { PARAM_CONFIG, DEPTHS, ParamKey, WEATHER_PARAM_CONFIG, WeatherParamKey } from '../../data/demo';
import type { AllThresholds } from '../../hooks/useThresholds';
import type { AllWeatherThresholds } from '../../hooks/useWeatherThresholds';

type Section = 'soil' | 'weather';

export default function ThresholdsPage() {
  const { t } = useTranslation();
  const [section, setSection] = useState<Section>('soil');

  return (
    <div className="page">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{t('thresholds.title')}</h1>
        <p style={{ fontSize: 13, color: 'var(--gray)' }}>
          Set acceptable ranges per parameter. Values outside range trigger Warning or Alert status.
        </p>
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
        {(['soil', 'weather'] as Section[]).map(s => (
          <button
            key={s}
            onClick={() => setSection(s)}
            style={{
              padding: '7px 20px', borderRadius: 8, border: '1.5px solid',
              borderColor: section === s ? 'var(--green)' : 'var(--border)',
              background: section === s ? 'var(--green-bg)' : 'transparent',
              color: section === s ? 'var(--green-dk)' : 'var(--gray)',
              fontSize: 13, cursor: 'pointer', fontWeight: section === s ? 600 : 400,
              transition: 'all 0.15s',
            }}
          >
            {s === 'soil' ? '🌱 Soil Sensors' : '🌤 Weather Stations'}
          </button>
        ))}
      </div>

      {section === 'soil'    && <SoilThresholds />}
      {section === 'weather' && <WeatherThresholds />}
    </div>
  );
}

// ── Soil Thresholds ───────────────────────────────────────────

function SoilThresholds() {
  const { t } = useTranslation();
  const { thresholds, save, reset } = useThresholds();
  const [draft, setDraft] = useState<AllThresholds>(() => JSON.parse(JSON.stringify(thresholds)));
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState<ParamKey | ''>('humidity');

  const handleChange = (param: ParamKey, depthIdx: number, field: 'lo' | 'hi', raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev)) as AllThresholds;
      next[param][depthIdx][field] = val;
      return next;
    });
  };

  const handleSave = () => { save(draft); setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const handleReset = () => { reset(); setDraft(JSON.parse(JSON.stringify(thresholds))); };

  return (
    <>
      <InfoBanner text="Values outside the range trigger Warning alerts. Values 15% beyond trigger Alert (red) status." />

      <div style={{ maxWidth: 680, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        {PARAM_CONFIG.map(p => {
          const isOpen = expanded === p.key;
          return (
            <div key={p.key} style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: 'var(--white)' }}>
              <div
                onClick={() => setExpanded(isOpen ? '' : p.key as ParamKey)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', cursor: 'pointer', background: isOpen ? 'var(--bg)' : 'transparent' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <ParamIcon icon={p.icon} size={20} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{p.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray)' }}>{p.unit} · {DEPTHS.length} depth levels</div>
                  </div>
                </div>
                <span style={{ fontSize: 12, color: 'var(--gray)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>▾</span>
              </div>

              {isOpen && (
                <div style={{ padding: '0 18px 18px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '8px 12px', marginTop: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('thresholds.depth')}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Min ({p.unit})</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Max ({p.unit})</div>
                    {DEPTHS.map((d, i) => (
                      <>
                        <div key={`d-${d}`} style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: 'var(--gray)', fontWeight: 500 }}>{d} cm</div>
                        <input key={`lo-${d}`} type="number" className="settings-input"
                          value={draft[p.key as ParamKey]?.[i]?.lo ?? ''}
                          onChange={e => handleChange(p.key as ParamKey, i, 'lo', e.target.value)}
                          style={{ padding: '6px 10px', fontSize: 13, borderColor: 'var(--amber)' }} />
                        <input key={`hi-${d}`} type="number" className="settings-input"
                          value={draft[p.key as ParamKey]?.[i]?.hi ?? ''}
                          onChange={e => handleChange(p.key as ParamKey, i, 'hi', e.target.value)}
                          style={{ padding: '6px 10px', fontSize: 13, borderColor: 'var(--red)' }} />
                      </>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <SaveResetRow saved={saved} onSave={handleSave} onReset={handleReset} />
    </>
  );
}

// ── Weather Thresholds ────────────────────────────────────────

function WeatherThresholds() {
  const { t } = useTranslation();
  const { thresholds, save, reset } = useWeatherThresholds();
  const [draft, setDraft] = useState<AllWeatherThresholds>(() => JSON.parse(JSON.stringify(thresholds)));
  const [saved, setSaved] = useState(false);

  const handleChange = (key: WeatherParamKey, field: 'lo' | 'hi', raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setDraft(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }));
  };

  const handleSave = () => { save(draft); setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const handleReset = () => { reset(); setDraft(JSON.parse(JSON.stringify(thresholds))); };

  return (
    <>
      <InfoBanner text="Applies to all weather stations. Alerts fire when a reading stays outside the threshold for more than 10 minutes." />

      <div style={{ maxWidth: 680, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 140px', gap: 12, padding: '10px 18px', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('thresholds.parameter')}</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Min</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Max</div>
        </div>

        {WEATHER_PARAM_CONFIG.map((p, idx) => (
          <div
            key={p.key}
            style={{
              display: 'grid', gridTemplateColumns: '1fr 140px 140px', gap: 12,
              padding: '12px 18px', alignItems: 'center',
              borderBottom: idx < WEATHER_PARAM_CONFIG.length - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>{p.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{p.label}</div>
                <div style={{ fontSize: 11, color: 'var(--gray)' }}>{p.unit}</div>
              </div>
            </div>
            <input
              type="number" className="settings-input"
              value={draft[p.key]?.lo ?? ''}
              onChange={e => handleChange(p.key, 'lo', e.target.value)}
              style={{ padding: '6px 10px', fontSize: 13, borderColor: 'var(--amber)' }}
            />
            <input
              type="number" className="settings-input"
              value={draft[p.key]?.hi ?? ''}
              onChange={e => handleChange(p.key, 'hi', e.target.value)}
              style={{ padding: '6px 10px', fontSize: 13, borderColor: 'var(--red)' }}
            />
          </div>
        ))}
      </div>

      <SaveResetRow saved={saved} onSave={handleSave} onReset={handleReset} />
    </>
  );
}

// ── Shared ────────────────────────────────────────────────────

function InfoBanner({ text }: { text: string }) {
  return (
    <div style={{
      background: 'var(--green-bg)', border: '1px solid var(--green)', borderRadius: 10,
      padding: '10px 14px', marginBottom: 20, fontSize: 12, color: 'var(--green-dk)',
      display: 'flex', gap: 8, alignItems: 'center', maxWidth: 680,
    }}>
      <span>ℹ️</span><span>{text}</span>
    </div>
  );
}

function SaveResetRow({ saved, onSave, onReset }: { saved: boolean; onSave: () => void; onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <button onClick={onSave} className="primary-button" style={{ padding: '9px 28px', fontSize: 13 }}>
        {saved ? '✓ Saved' : 'Save Changes'}
      </button>
      <button onClick={onReset} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', fontSize: 13, color: 'var(--gray)', cursor: 'pointer' }}>
        {t('thresholds.reset')}
      </button>
    </div>
  );
}
